import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, File as FileIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function SubmitAssets() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [property, setProperty] = useState({ name: "", url: "", address: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => {
      setOrder(data);
      if (data.property) setProperty({ ...{ name: "", url: "", address: "", notes: "" }, ...data.property });
    }).catch(() => navigate("/dashboard"));
  }, [id, navigate]);

  const onUpload = async (e) => {
    const chosen = Array.from(e.target.files || []);
    if (!chosen.length) return;
    setUploading(true);
    try {
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const startProduction = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/assets`, { property, asset_ids: files.map((f) => f.id) });
      toast.success("Production started");
      navigate(`/orders/${id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (!order) return <div className="bg-[#090A0F] min-h-screen"><Header /></div>;

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-3xl mx-auto px-6 pt-32 pb-16 fade-up">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Order {order.order_number} · Submit Assets</p>
        <h1 className="font-display text-4xl text-white mb-2">{order.product_name}</h1>
        <p className="text-slate-400 mb-8">Provide a listing link or upload photos, renderings and plans. Production begins as soon as you submit.</p>

        <div className="space-y-4 mb-8">
          <div>
            <Label className="text-slate-300">Property / listing name</Label>
            <Input value={property.name} onChange={(e) => setProperty({ ...property, name: e.target.value })}
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="submit-property-name" />
          </div>
          <div>
            <Label className="text-slate-300">Listing / property URL</Label>
            <Input value={property.url} onChange={(e) => setProperty({ ...property, url: e.target.value })}
              placeholder="https://…" className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="submit-property-url" />
          </div>
          <div>
            <Label className="text-slate-300">Notes</Label>
            <Textarea value={property.notes} onChange={(e) => setProperty({ ...property, notes: e.target.value })}
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="submit-property-notes" />
          </div>
        </div>

        <label className="block border-2 border-dashed border-[#D4AF37]/25 rounded-2xl p-10 text-center cursor-pointer hover:border-[#D4AF37]/50 transition-all bg-[#12141D]/50" data-testid="upload-dropzone">
          <input type="file" multiple className="hidden" onChange={onUpload} accept="image/*,application/pdf" data-testid="file-input" />
          {uploading ? <Loader2 className="w-8 h-8 text-[#D4AF37] mx-auto animate-spin" /> : <UploadCloud className="w-8 h-8 text-[#D4AF37] mx-auto" />}
          <p className="text-white mt-3">Click to upload photos, renderings or plans</p>
          <p className="text-xs text-slate-500 mt-1">Images & PDF</p>
        </label>

        {files.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="uploaded-files">
            {files.map((f) => (
              <div key={f.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-[#181B26] border border-slate-800">
                <span className="flex items-center gap-2 text-sm text-slate-300"><FileIcon className="w-4 h-4 text-[#D4AF37]" /> {f.original_filename}</span>
                <button onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}

        <Button onClick={startProduction} disabled={busy}
          className="w-full mt-8 bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full h-12" data-testid="start-production-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit & Start Production"}
        </Button>
      </div>
      <Footer />
    </div>
  );
}
